import React, { useState } from "react";
import {
  PRIMARY_OBJECTIVE_OPTIONS,
  RISK_TOLERANCE_OPTIONS,
  DECISION_MAKING_OPTIONS,
  SOURCE_OF_FUNDS_OPTIONS,
  PRIMARY_USE_OPTIONS,
  TIME_HORIZON_OPTIONS,
  TIME_HORIZON_MAP,
  getAccountRiskTimeHorizonLabel,
} from "../../../../constants/householdConstants";
import {
  LIQUIDITY_NEEDS_OPTIONS,
  LIQUIDITY_TIME_HORIZON_OPTIONS,
  parseLiquidityNeedsDisplay,
  parseLiquidityTimeHorizonDisplay,
  getLiquidityNeedsLabel,
  getLiquidityTimeHorizonLabel,
} from "../../../../constants/accountConstants";
import {
  getBrokerageFlags,
  showSourceOfFunds,
  showUseOfFunds,
  showLiquidityRequirements,
} from "../../../../utils/brokerageFields";

const ALL_FIELDS = [
  { key: "invest_objectives", label: "Primary Investment Objective", options: PRIMARY_OBJECTIVE_OPTIONS, isArray: true },
  { key: "risk_tolerance", label: "Risk Tolerance", options: RISK_TOLERANCE_OPTIONS },
  { key: "time_horizon", label: "Time Horizon", options: TIME_HORIZON_OPTIONS, isRange: true },
  { key: "decision_making", label: "Account Decision Making", options: DECISION_MAKING_OPTIONS },
  { key: "source_of_funds", label: "Source of Funds", options: SOURCE_OF_FUNDS_OPTIONS, isArray: true },
  { key: "use_of_funds", label: "Primary Use of Funds", options: PRIMARY_USE_OPTIONS },
  { key: "liquidity_requirements.amount", label: "Liquidity Needs ($)", options: LIQUIDITY_NEEDS_OPTIONS, isRange: true },
  { key: "liquidity_requirements.time_horizon", label: "Liquidity Time Horizon (years)", options: LIQUIDITY_TIME_HORIZON_OPTIONS, isRange: true },
];

const getValueByPath = (obj, path) =>
  path.split(".").reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);

const setValueByPath = (obj, path, value) => {
  const keys = path.split(".");
  const root = { ...obj };
  let current = root;
  for (let i = 0; i < keys.length - 1; i += 1) {
    const key = keys[i];
    current[key] = { ...(current[key] || {}) };
    current = current[key];
  }
  current[keys[keys.length - 1]] = value;
  return root;
};

const formatTimeHorizon = (range) => {
  if (!range || range.start === null || range.start === undefined || range.end === null || range.end === undefined) return "";
  const entry = Object.entries(TIME_HORIZON_MAP).find(
    ([, value]) =>
      Number(value.start) === Number(range.start) && Number(value.end) === Number(range.end),
  );
  if (entry) return entry[0];
  return getAccountRiskTimeHorizonLabel(range, "");
};

const parseTimeHorizon = (label) => {
  if (!label) return null;
  return TIME_HORIZON_MAP[label] || null;
};

const isMissingField = (val, isArray, isRange) => {
  if (isArray) return !Array.isArray(val) || val.length === 0;
  if (isRange) {
    return (
      !val ||
      val.start === null ||
      val.start === undefined ||
      val.end === null ||
      val.end === undefined
    );
  }
  return val === null || val === undefined || val === "";
};

const RiskToleranceForm = ({ householdData, fieldData, missingFields, entityId, onSave, onSaveAndExit, saving, nextLabel }) => {
  const account = fieldData || {};
  const existing = account.risk_tolerance || {};

  const brokerageFlags = getBrokerageFlags(householdData);
  const isFieldAllowed = (key) => {
    if (key === "source_of_funds") return showSourceOfFunds(brokerageFlags);
    if (key === "use_of_funds") return showUseOfFunds(brokerageFlags);
    if (
      key === "liquidity_requirements.amount" ||
      key === "liquidity_requirements.time_horizon"
    ) {
      return showLiquidityRequirements(brokerageFlags);
    }
    return true;
  };

  const fieldsToShow = ALL_FIELDS.filter(
    (f) =>
      isFieldAllowed(f.key) &&
      isMissingField(getValueByPath(existing, f.key), f.isArray, f.isRange),
  );
  const missingCount = fieldsToShow.length;

  const [formData, setFormData] = useState(() => {
    const initial = {};
    fieldsToShow.forEach((f) => {
      const currentValue = getValueByPath(existing, f.key);
      if (f.isArray) {
        initial[f.key] = Array.isArray(currentValue) ? currentValue[0] || "" : "";
      } else if (f.key === "time_horizon") {
        initial[f.key] = formatTimeHorizon(currentValue);
      } else if (f.key === "liquidity_requirements.amount") {
        initial[f.key] = getLiquidityNeedsLabel(currentValue, "");
      } else if (f.key === "liquidity_requirements.time_horizon") {
        initial[f.key] = getLiquidityTimeHorizonLabel(currentValue, "");
      } else {
        initial[f.key] = currentValue || "";
      }
    });
    return initial;
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const buildPayload = () => {
    let risk_tolerance = { ...existing };
    fieldsToShow.forEach((f) => {
      const val = formData[f.key];
      if (f.isArray) {
        risk_tolerance[f.key] = val ? [val] : [];
      } else if (f.key === "time_horizon") {
        risk_tolerance.time_horizon = val ? parseTimeHorizon(val) : null;
      } else if (f.key === "liquidity_requirements.amount") {
        risk_tolerance = setValueByPath(
          risk_tolerance,
          "liquidity_requirements.amount",
          val ? parseLiquidityNeedsDisplay(val) : null,
        );
      } else if (f.key === "liquidity_requirements.time_horizon") {
        risk_tolerance = setValueByPath(
          risk_tolerance,
          "liquidity_requirements.time_horizon",
          val ? parseLiquidityTimeHorizonDisplay(val) : null,
        );
      } else {
        risk_tolerance[f.key] = val || null;
      }
    });
    return { accounts: { [entityId]: { risk_tolerance } } };
  };

  const handleSubmit = (cb) => cb(buildPayload());

  if (missingCount === 0) {
    return (
      <div className="text-center py-6 text-gray-500">
        <p className="text-sm">All investment strategy fields have been filled.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-semibold text-gray-900">Missing Information</h2>
        <span className="text-sm text-red-500 font-medium">
          ● {missingCount} Required field{missingCount !== 1 ? "s" : ""} missing
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
        {fieldsToShow.map((f) => (
          <div key={f.key}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {f.label} <span className="text-red-500">*</span>
            </label>
            <select value={formData[f.key]} onChange={(e) => handleChange(f.key, e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500">
              <option value="">Select...</option>
              {f.options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between mt-8 pt-6 border-t border-gray-200">
        <button onClick={() => handleSubmit(onSaveAndExit)} disabled={saving} className="px-6 py-2.5 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 transition-colors disabled:opacity-50">Save & Exit</button>
        <button onClick={() => handleSubmit(onSave)} disabled={saving} className="px-6 py-2.5 rounded-lg text-sm font-medium bg-emerald-500 text-white hover:bg-emerald-600 transition-colors disabled:opacity-50 flex items-center gap-2">
          {saving && <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>}
          {nextLabel || "Save & Next →"}
        </button>
      </div>
    </div>
  );
};

export default RiskToleranceForm;
